import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Badge, Field, NumberInput, Segmented, StatePanel } from "../../components/ui";
import { requireResponse } from "../../core/errors/requireResponse";
import { int, money, moneyShort, pct, vsLabel } from "../format";
import { BarList, Donut, Heatmap, Legend } from "../components/charts";
import { DashCard, Delta, InfoTip, ListRow, Rank } from "../components/parts";
import { cardState } from "../components/cardState";
import { SILENT, dashKey, useDashboardSettings } from "../useDashboardData";

// Executive tab (store owner). Each card reads its own part of
// /api/dashboard/executive (?part=margin|products|portfolio|cltv|wa).

function Margin({ q }) {
  const m = q.data?.margin || {};
  const state = cardState(q, { what: "the margin" });
  const revenue = Number(m.revenue_pence) || 0;
  const opex = m.opex || {};
  const opexTotal = ["polishing", "accessories", "shipping", "travel"].reduce((s, k) => s + (Number(opex[k]) || 0), 0);
  const share = (v) => (revenue ? Math.max(0, (Number(v) || 0) / revenue) * 100 : 0);
  const cogsW = share(m.cogs_pence);
  const opexW = share(opexTotal);
  const commW = share(m.commission_pence);
  const marginW = Math.max(0, 100 - cogsW - opexW - commW);
  const deltaPts = m.margin_pct != null && m.prev_margin_pct != null ? m.margin_pct - m.prev_margin_pct : null;
  const vs = vsLabel(q.data?.period?.prev_label) || "vs the previous period";
  return (
    <DashCard title="Gross margin" info="margin" meta={q.data?.period?.label}>
      {state ||
        (revenue ? (
          <>
            <div className="dash-big">
              <b className="dash-num">{pct(m.margin_pct, 1)}</b>
              <span className="dash-num">{money(m.margin_pence)} on {moneyShort(revenue)} revenue</span>
              {deltaPts != null ? (
                <span className="dash-big__delta">
                  <Delta value={deltaPts} suffix=" pts" label={vs} />
                  <InfoTip k="marginPts" />
                </span>
              ) : null}
            </div>
            {deltaPts != null ? (
              <p className="dash-muted dash-big__note">
                {pct(m.margin_pct, 1)} now against {pct(m.prev_margin_pct, 1)} {vs.replace(/^vs /, "")} — pts = percentage points.
              </p>
            ) : null}
            <svg className="dash-mgbar" viewBox="0 0 100 10" preserveAspectRatio="none" role="img" aria-label={`COGS ${Math.round(cogsW)}%, OpEx ${Math.round(opexW)}%, commission ${Math.round(commW)}%, margin ${Math.round(marginW)}%`}>
              <rect className="dash-fill--c6" x="0" y="0" width={cogsW} height="10" />
              <rect className="dash-fill--c3" x={cogsW} y="0" width={opexW} height="10" />
              <rect className="dash-fill--c4" x={cogsW + opexW} y="0" width={commW} height="10" />
              <rect className="dash-fill--good" x={cogsW + opexW + commW} y="0" width={marginW} height="10" />
            </svg>
            <Legend
              format={moneyShort}
              rows={[
                { name: "Polishing", value: opex.polishing },
                { name: "Accessories", value: opex.accessories },
                { name: "Shipping", value: opex.shipping },
                { name: "Travel", value: opex.travel },
                { name: "Commission", value: m.commission_pence },
                ...(m.expert_fee_pence ? [{ name: "Expert fees (added)", value: m.expert_fee_pence }] : []),
                { name: "COGS", value: m.cogs_pence },
              ]}
            />
          </>
        ) : (
          <p className="dash-empty">No revenue in this period yet.</p>
        ))}
    </DashCard>
  );
}

const toPence = (major) => (major == null || major === "" ? null : Math.round(Number(major) * 100));
const toMajor = (pence) => (pence == null ? null : Number(pence) / 100);

/**
 * Customer lifetime value: the formula with its live inputs, and — for the
 * owner — the two acquisition-cost inputs (CAC per new client, or monthly
 * acquisition spend → derived CAC). Auto-saved (PUT /api/dashboard/settings),
 * then the cltv part is re-fetched.
 */
function Cltv({ q, storeKey, canEdit }) {
  const qc = useQueryClient();
  const c = q.data?.cltv || {};
  const settings = useDashboardSettings(storeKey, canEdit);
  const s = settings.data || {};
  const [cac, setCac] = useState(null);
  const [spend, setSpend] = useState(null);
  useEffect(() => {
    setCac(toMajor(s.cac_pence));
    setSpend(toMajor(s.monthly_acquisition_spend_pence));
  }, [s.cac_pence, s.monthly_acquisition_spend_pence]);

  const save = async (patch) => {
    const res = requireResponse(await axios.put("/api/dashboard/settings", patch, SILENT));
    qc.setQueryData(dashKey("settings", storeKey), res.data);
    qc.invalidateQueries({ queryKey: ["dashboard", "executive", storeKey ?? "home"] });
    return res.data;
  };

  const state = cardState(q, { what: "lifetime value" });
  const effectiveCac = c.cac_pence ?? c.cac ?? s.effective_cac_pence ?? null;
  const derived = s.derived_cac_pence ?? (spend && c.avg_new_clients_per_month ? Math.round((spend * 100) / c.avg_new_clients_per_month) : null);
  const years = c.avg_lifespan_years ?? (c.avg_lifespan_days != null ? c.avg_lifespan_days / 365 : null);
  const ppy = c.purchases_per_client_per_year;
  const inputs = [
    ["Average order value", c.aov_pence != null ? moneyShort(c.aov_pence) : null],
    ["Purchases per client per year", ppy != null ? Number(ppy).toFixed(2) : null],
    ["Average customer lifespan", years != null ? `${Number(years).toFixed(1)} yrs` : null],
    ["Customer acquisition cost", effectiveCac != null ? `${moneyShort(effectiveCac)}${c.cac_source === "derived" ? " (from spend)" : ""}` : null],
  ];

  return (
    <DashCard title="Customer lifetime value" info="cltv" meta={c.window || null}>
      {state || (
        <div className="dash-cltv">
          <div className="dash-stack">
          <div className="dash-big">
            {c.ready && c.cltv_pence != null ? (
              <>
                <b className="dash-num">{moneyShort(c.cltv_pence)}</b>
                <span>per client</span>
              </>
            ) : (
              <>
                <b className="dash-num dash-subtle">—</b>
                <span>{effectiveCac == null ? "Add your acquisition cost to see it" : "Not enough data yet"}</span>
              </>
            )}
          </div>
          <p className="dash-formula dash-num">
            {moneyShort(c.aov_pence)} × {ppy != null ? Number(ppy).toFixed(2) : "—"} / yr × {years != null ? Number(years).toFixed(1) : "—"} yrs − {effectiveCac != null ? moneyShort(effectiveCac) : "CAC"}
            {c.ready && c.cltv_pence != null ? <> = <b>{moneyShort(c.cltv_pence)}</b></> : null}
          </p>
          <div className="dash-needs">
            {inputs.map(([label, v]) => (
              <div key={label}>
                <span>{label}</span>
                <Badge size="sm" tone={v ? "success" : "danger"}>{v || "Missing"}</Badge>
              </div>
            ))}
          </div>
          </div>
          {canEdit ? (
            <div className="dash-cac" aria-label="Acquisition cost">
              <Field label="CAC per new client" hint="What one new client costs you to win." onSave={(v) => save({ cac_pence: toPence(v) })} data-field="dashboard-cac">
                <NumberInput money min={0} size="md" value={cac} onChange={setCac} placeholder="e.g. 250" />
              </Field>
              <Field
                label="or monthly acquisition spend"
                hint={derived != null ? `= ${money(derived)} per new client (${int(c.avg_new_clients_per_month ?? s.avg_new_clients_per_month)} new a month)` : "Marketing + ads per month; CAC = spend ÷ new clients a month."}
                onSave={(v) => save({ monthly_acquisition_spend_pence: toPence(v) })}
                data-field="dashboard-acq-spend"
              >
                <NumberInput money min={0} size="md" value={spend} onChange={setSpend} placeholder="e.g. 2,000" />
              </Field>
              {cac != null && spend != null ? <p className="dash-muted">The CAC you entered wins over the one derived from spend.</p> : null}
            </div>
          ) : null}
        </div>
      )}
    </DashCard>
  );
}

function Missed({ wa }) {
  const [win, setWin] = useState("24h");
  // Round 2 sends missed_24h / missed_7d; an older API only `missed`.
  const list = (win === "24h" ? (wa.missed_24h !== undefined ? wa.missed_24h : wa.missed) : wa.missed_7d ?? wa.missed) || [];
  return (
    <DashCard
      title="Asked for, not in stock"
      info="missed"
      tools={<Segmented size="sm" label="Window" value={win} onChange={setWin} options={[{ value: "24h", label: "24h" }, { value: "7d", label: "7 days" }]} />}
    >
      {list.length ? (
        <div className="dash-list">
          {list.slice(0, 6).map((m, i) => (
            <ListRow key={`${m.product_id ?? m.name}-${i}`} lead={<Rank n={i + 1} />} title={m.name} sub={m.askers != null ? `${int(m.askers)} asking` : null} right={int(m.mentions)} rightSub="mentions" />
          ))}
        </div>
      ) : (
        <p className="dash-empty">No buy requests for out-of-stock watches in the last {win === "24h" ? "24 hours" : "7 days"}.</p>
      )}
    </DashCard>
  );
}

function WhatsApp({ q }) {
  const wa = q.data?.wa || {};
  const [range, setRange] = useState("day");
  const state = cardState(q, { what: "WhatsApp activity" });
  if (wa.status === "not_available") return null;
  if (state) return <DashCard title="Inbound WhatsApp by hour" info="heat">{state}</DashCard>;
  if (wa.status === "error") {
    return (
      <DashCard title="Inbound WhatsApp by hour" info="heat">
        <StatePanel compact state="error" title="WhatsApp data unavailable" description="The message store did not answer. Try again later." onRetry={() => q.refetch()} />
      </DashCard>
    );
  }
  const rows = range === "day" ? (wa.heatmap_24h ? [wa.heatmap_24h] : []) : wa.heatmap_7d || [];
  const flat = rows.flat().map((v) => Number(v) || 0);
  const top = flat.length ? Math.max(...flat) : 0;
  const peak = top > 0 ? flat.indexOf(top) % 24 : null;
  const total = flat.reduce((a, b) => a + b, 0);
  return (
    <div className="dash-grid dash-grid--main">
      <DashCard
        title="Inbound WhatsApp by hour"
        info="heat"
        tools={<Segmented size="sm" label="Heat map range" value={range} onChange={setRange} options={[{ value: "day", label: "24h" }, { value: "week", label: "7 days" }]} />}
      >
        {rows.length ? (
          <>
            <Heatmap rows={rows} />
            {peak != null ? (
              <div className="dash-callout">
                Peak <b>{String(peak).padStart(2, "0")}:00–{String((peak + 1) % 24).padStart(2, "0")}:00</b> with {int(top)} messages · {int(total)} inbound {range === "day" ? "in the last 24h" : "in 7 days"}.
              </div>
            ) : null}
          </>
        ) : (
          <p className="dash-empty">No inbound messages in this window.</p>
        )}
      </DashCard>
      <Missed wa={wa} />
    </div>
  );
}

function TopProducts({ q }) {
  const [by, setBy] = useState("revenue");
  const tp = q.data?.top_products || {};
  const list = (by === "revenue" ? tp.by_revenue : tp.by_won) || [];
  const state = cardState(q, { what: "top products", empty: !list.length, emptyText: "No products sold in this period." });
  return (
    <DashCard
      title="Top performing products"
      info="topProducts"
      tools={<Segmented size="sm" label="Rank by" value={by} onChange={setBy} options={[{ value: "revenue", label: "Revenue" }, { value: "won", label: "Deals" }]} />}
    >
      {state || (
        <div className="dash-list">
          {list.slice(0, 5).map((p, i) => (
            <ListRow key={`${p.product_id}-${i}`} lead={<Rank n={i + 1} />} title={p.name} sub={`${int(p.won)} won`} right={moneyShort(p.revenue_pence)} />
          ))}
        </div>
      )}
    </DashCard>
  );
}

function TopProfit({ q }) {
  const list = q.data?.top_profit || [];
  const state = cardState(q, { what: "top products by profit", empty: !list.length, emptyText: "No products sold in this period." });
  return (
    <DashCard title="Top products by profit" info="topProfit" meta="by margin %">
      {state || (
        <div className="dash-list">
          {list.slice(0, 5).map((p, i) => (
            <ListRow key={`${p.product_id}-${i}`} lead={<Rank n={i + 1} />} title={p.name} sub={`${moneyShort(p.profit_pence)} profit`} right={<span className="dash-good">{pct(p.margin_pct, 1)}</span>} />
          ))}
        </div>
      )}
    </DashCard>
  );
}

function Portfolio({ q }) {
  const p = q.data?.portfolio || {};
  const rows = [...(p.reps || []).map((r) => ({ name: r.name, value: r.count })), ...(p.unassigned?.count ? [{ name: "Unassigned", value: p.unassigned.count }] : [])];
  const state = cardState(q, { what: "the portfolio", empty: !rows.length, emptyText: "No clients yet." });
  const assigned = p.unassigned?.pct != null ? 100 - p.unassigned.pct : null;
  return (
    <DashCard title="Client portfolio allocation" info="portfolio" meta={p.total_contacts != null ? `${int(p.total_contacts)} clients` : null}>
      {state || <Donut rows={rows} center={assigned != null ? pct(assigned) : "—"} sub="assigned" />}
    </DashCard>
  );
}

/** `parts` = { margin, products, portfolio, cltv, wa } — one query each. */
export default function ExecutiveTab({ parts, waAvailable, storeKey, canEdit }) {
  const pq = parts.products;
  const b = pq.data || {};
  const brands = (b.brands || []).map((x) => ({ name: x.name, value: x.units }));
  const models = (b.models || []).map((x) => ({ name: x.name, value: x.units }));
  const denied = Object.values(parts).find((p) => p.isError && p.error?.response?.status === 403);
  if (denied) {
    return <StatePanel state="permission" title="Executive view is for the store owner" description={denied.error?.response?.data?.message || "Ask the store owner if you need these numbers."} />;
  }
  return (
    <div className="dash-stack dash-stack--page">
      <div className="dash-grid dash-grid--2 dash-grid--eq">
        <Margin q={parts.margin} />
        <Portfolio q={parts.portfolio} />
      </div>
      {waAvailable ? <WhatsApp q={parts.wa} /> : null}
      <div className="dash-grid dash-grid--2 dash-grid--eq">
        <TopProducts q={pq} />
        <TopProfit q={pq} />
      </div>
      <div className="dash-grid dash-grid--2 dash-grid--eq">
        <DashCard title="Top 10 brands" info="brands" meta="units sold">
          {cardState(pq, { what: "brands", empty: !brands.length, emptyText: "No units sold in this period." }) || <BarList rows={brands} format={int} />}
        </DashCard>
        <DashCard title="Top 10 models" info="brands" meta="units sold">
          {cardState(pq, { what: "models", empty: !models.length, emptyText: "No units sold in this period." }) || <BarList rows={models} format={int} tone={2} />}
        </DashCard>
      </div>
      <Cltv q={parts.cltv} storeKey={storeKey} canEdit={canEdit} />
    </div>
  );
}
